/**
 * 矢印を、乗せても離しても同じ向きへ抜けさせる
 *
 * @description 枠の中に矢印を2本重ね、居るほうを枠の外へ送り出しながら、待っていたほうを
 *              反対側から入れる。CSSのホバーだけで作ると離したときに逆再生になってしまうので、
 *              入りと出のどちらでも同じ1往復を最初から流す。
 *
 *              枠の外へ出す量は画面幅で変わるため、数はSCSSの --arrow-slide が持つ。
 *              ここは向きの符号と軸だけを掛けて、実寸には触らない。
 */

/** 1本が枠を通り抜けるのにかける時間 */
const DUR = 300;

/** 送り出してから次の1本が入り始めるまでの間。少し遅らせると2本が繋がって見える */
const LAG = 60;

/** 送り出した1本を待機側へ戻すまでの余裕。transitionの終わりを踏まないための足しぶん */
const REARM = 60;

/** 抜ける速さの曲線。入りは速く、出際をゆっくり流す */
const EASE = 'cubic-bezier(0.08, 0.7, 0.58, 1)';

/**
 * 色の遷移
 *
 * @description 動きのために transition を書き換えると、矢印自身が持っていた $hover ごと
 *              消える。色だけは巻き添えにしないよう、こちらでも並べて持つ
 */
const TINT = 'background-color .35s ease';

/** 動かす軸 */
type Axis = 'x' | 'y';

/** 矢印を仕込む相手 */
type Spec = {
    /** ホバー・フォーカスを受ける側 */
    host: string;
    /** その中に2本並ぶ矢印 */
    arrow: string;
    /** 抜ける向き。1なら右、縦軸なら下 */
    dir: number;
    /** 動かす軸 */
    axis: Axis;
    /**
     * 最初に枠の中に居るほうの添字
     *
     * @description 多くは attr-1 が見えていて attr-2 が待つので0。.Btn だけは
     *              矢印が別々の入れ物に入っていて、円の枠を持つ側が見えている
     */
    home?: number;
};

/**
 * 仕込む相手の一覧
 *
 * @description デザイン上この動きを持つ矢印は、円や枠で切って2本を入れ替える形で共通している。
 *              ここに無い矢印（FVのScroll Downなど）はホバーで動かすものではない
 */
const SPECS: Spec[] = [
    { host: '.Btn.type-circle', arrow: '.Btn__innerIconArrow', dir: 1, axis: 'x', home: 1 },
    { host: '.Btn.type-back', arrow: '.Btn__innerIconArrow', dir: -1, axis: 'x' },
    { host: '.Formbtn.attr-right', arrow: '.Formbtn__innerIconArrow', dir: 1, axis: 'x' },
    { host: '.Formbtn.attr-left', arrow: '.Formbtn__innerIconArrow', dir: -1, axis: 'x' },
    { host: '.Newscard', arrow: '.Newscard__cntHeadBtnInner', dir: 1, axis: 'x' },
    { host: '.Newstile', arrow: '.Newstile__cntHeadBtnArrow', dir: 1, axis: 'x' },
    { host: '.Pager__nav.attr-next', arrow: '.Pager__navArrow', dir: 1, axis: 'x' },
    { host: '.Pager__nav.attr-prev', arrow: '.Pager__navArrow', dir: -1, axis: 'x' },
    { host: '.insta__bodyNavBtn.attr-next', arrow: '.insta__bodyNavBtnArrow', dir: 1, axis: 'x' },
    { host: '.insta__bodyNavBtn.attr-prev', arrow: '.insta__bodyNavBtnArrow', dir: -1, axis: 'x' },
    { host: '.Pagemv__cntNavListItemLink', arrow: '.Pagemv__cntNavListItemLinkIconArrow', dir: 1, axis: 'y' },
    { host: '.artist__navListItemLink', arrow: '.artist__navListItemLinkIconArrow', dir: 1, axis: 'y' },
];

/** 仕込み済みの印。非同期遷移で同じ器を2度なぞっても重ねて付けない */
const DONE = 'data-arrowpass';

/** 1つ分の進み具合 */
type Pass = {
    /** いま枠の中に居るほうの添字 */
    home: number;
    /** 次の1往復を始めてよい時刻 */
    until: number;
    /** 順番待ちを二重に積まないための印 */
    queued: boolean;
};

/**
 * 矢印を置く
 *
 * @description 待機側へ戻すときは transition を切る。切った直後にまた動かすので、
 *              ここで一度描かせておかないと戻りの移動まで滑ってしまう
 * @param el 矢印
 * @param dir -1 / 0 / 1。--arrow-slide に掛ける向き
 * @param axis 動かす軸
 * @param animate 滑らせるなら true
 * @returns {void}
 */
const place = (el: HTMLElement, dir: number, axis: Axis, animate: boolean): void => {
    const slide = `calc(var(--arrow-slide) * ${dir})`;

    el.style.transition = animate ? `translate ${DUR}ms ${EASE}, ${TINT}` : 'none';
    el.style.translate = dir === 0 ? '0 0' : axis === 'y' ? `0 ${slide}` : `${slide} 0`;

    if (animate) return;

    // 切ったままにすると、この瞬間に色が変わるとき飛んで見える。描かせてから色だけ戻す
    void el.offsetWidth;
    el.style.transition = TINT;
};

/**
 * 矢印を1往復させる
 *
 * @description 前の往復が終わる前に呼ばれたら、終わる時刻まで待って流し直す。
 *              待ちは1つだけ積む
 * @param arrows 2本の矢印
 * @param spec 仕込む相手の決まり
 * @param state 進み具合
 * @returns {void}
 */
const pass = (arrows: HTMLElement[], spec: Spec, state: Pass): void => {
    const now = performance.now();

    if (now < state.until) {
        if (state.queued) return;

        state.queued = true;
        window.setTimeout(() => {
            state.queued = false;
            pass(arrows, spec, state);
        }, state.until - now);

        return;
    }

    state.until = now + DUR + LAG + REARM;

    const out = arrows[state.home];
    const inn = arrows[1 - state.home];

    state.home = 1 - state.home;

    place(out, spec.dir, spec.axis, true);
    window.setTimeout(() => place(inn, 0, spec.axis, true), LAG);
    window.setTimeout(() => place(out, -spec.dir, spec.axis, false), DUR + REARM);
};

/**
 * 1つの相手に仕込む
 *
 * @param host ホバーを受ける側
 * @param spec 仕込む相手の決まり
 * @returns {void}
 */
const arm = (host: HTMLElement, spec: Spec): void => {
    if (host.hasAttribute(DONE)) return;

    const arrows = [...host.querySelectorAll<HTMLElement>(spec.arrow)];

    if (arrows.length !== 2) return;

    host.setAttribute(DONE, '');

    const state: Pass = { home: spec.home ?? 0, until: 0, queued: false };

    place(arrows[state.home], 0, spec.axis, false);
    place(arrows[1 - state.home], -spec.dir, spec.axis, false);

    const run = (): void => pass(arrows, spec, state);

    host.addEventListener('mouseenter', run);
    host.addEventListener('mouseleave', run);
    host.addEventListener('focus', run);
    host.addEventListener('blur', run);
};

/**
 * 矢印を仕込む
 *
 * @param scope 新しい器。非同期遷移の最中は古い器も残っているので探索範囲を絞る
 * @returns {void}
 */
const initBtnArrow = (scope: ParentNode = document): void => {
    SPECS.forEach((spec) => {
        scope.querySelectorAll<HTMLElement>(spec.host).forEach((host) => arm(host, spec));
    });
};

export { initBtnArrow };

document.addEventListener('DOMContentLoaded', () => initBtnArrow());
